import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, useToast,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Tag, Icon,
  InputGroup, InputLeftElement, Input
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, SearchIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiEye, FiMessageCircle, FiAlertCircle, FiRefreshCw, FiBriefcase } from 'react-icons/fi';
import AdminChatModal from '../AdminChat/AdminChatModal';
import WorkerCancellationDetailsModal from './components/WorkerCancellationDetailsModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const WorkerCancellation = () => {
  const [workersCancellation, setWorkersCancellation] = useState([]);
  const [filteredCancellations, setFilteredCancellations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWorkerCancellation, setSelectedWorkerCancellation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const toast = useToast();

  // Color scheme
  const tableBorderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const filterBg = useColorModeValue('white', 'gray.800');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch workers cancellations on mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchWorkersCancellations();
  }, []);

  // Filter cancellations when data, search term, or status filter changes
  useEffect(() => {
    applyFilters();
  }, [workersCancellation, searchTerm, statusFilter]);

  // Function to apply filters
  const applyFilters = () => {
    let result = [...workersCancellation];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.cancellationOption && item.cancellationOption.toLowerCase().includes(searchLower)) || 
        (item.reason && item.reason.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          result = result.filter(item => !item.actionTaken); // All non-completed
          break;
        case 'pending':
          result = result.filter(item => !item.inProgress && !item.actionTaken);
          break;
        case 'inProgress':
          result = result.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation);
          break;
        case 'pendingConfirmation':
          result = result.filter(item => item.pendingConfirmation && !item.actionTaken);
          break;
        case 'completed':
          result = result.filter(item => item.actionTaken);
          break;
        default:
          break;
      }
    }
    
    setFilteredCancellations(result);
  };

  // Function to fetch cancellation list
  const fetchWorkersCancellations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/cancellation/worker/cancellationRequests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch workers');
      }

      const data = await response.json();
      setWorkersCancellation(data);
      setFilteredCancellations(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar trabalhadores',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
  };

  // Open chat modal with selected worker
  const openAdminChat = (userId) => {
    setCurrentUserId(userId);
    setIsChatModalOpen(true);
  };

  // Fetch cancellation request details by ID
  const fetchWorkerCancellationById = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/cancellation/worker/cancellationRequests/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch worker cancellation details');
      }

      const data = await response.json();

      // Auto-change to "Em Atendimento" if still in "Aguardando"
      if (data.success && data.data && !data.data.inProgress && !data.data.actionTaken) {
        // Call the PUT endpoint to update status
        await fetch(
          `${API_BASE_URL}api/cancellation/worker/confirmActionTaken/${id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ inProgress: true }),
          }
        );

        // Update local state
        const updatedData = { ...data.data, inProgress: true };
        setSelectedWorkerCancellation(updatedData);

        // Update the list as well
        const updatedCancellations = workersCancellation.map((wc) =>
          wc._id === id ? { ...wc, inProgress: true } : wc
        );
        setWorkersCancellation(updatedCancellations);
      } else {
        setSelectedWorkerCancellation(data.data);
      }

      setIsModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar detalhes do trabalhador',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Update status (Action Taken)
  const handleActionTaken = async (workerCancellationId) => {
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');

      // Send request to update backend
      const response = await fetch(
        `${API_BASE_URL}api/cancellation/worker/confirmActionTaken/${workerCancellationId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // USE BACKEND RESPONSE DATA instead of assuming values
        const updatedFields = {
          inProgress: result.data.inProgress,
          actionTaken: result.data.actionTaken,
          pendingConfirmation: result.data.pendingConfirmation
        };

        // Update the local state with actual backend data
        const updatedWorkers = workersCancellation.map((wc) =>
          wc._id === workerCancellationId ? { ...wc, ...updatedFields } : wc
        );
        setWorkersCancellation(updatedWorkers);

        // Also update the selected cancellation if it's currently open
        if (selectedWorkerCancellation && selectedWorkerCancellation._id === workerCancellationId) {
          setSelectedWorkerCancellation({
            ...selectedWorkerCancellation,
            ...updatedFields
          });
        }

        // Show appropriate message based on state
        if (result.pendingConfirmation) {
          toast({
            title: 'Aguardando Confirmação',
            description: 'Mensagem de confirmação enviada ao trabalhador',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Sucesso',
            description: result.message || 'Status atualizado com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Info',
          description: result.message || 'Não foi possível atualizar',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Get status badge
  const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
    let colorScheme, status, icon;

    if (isCompleted) {
      colorScheme = 'green';
      status = 'Concluído';
      icon = CheckIcon;
    } else if (isPendingConfirmation) {
      colorScheme = 'orange';
      status = 'Aguardando Confirmação';
      icon = QuestionIcon;
    } else if (isInProgress) {
      colorScheme = 'yellow';
      status = 'Em Andamento';
      icon = TimeIcon;
    } else {
      colorScheme = 'red';
      status = 'Aguardando';
      icon = WarningIcon;
    }

    return (
      <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
        <Flex align="center">
          <Icon as={icon} mr={1} boxSize="10px" />
          <Text>{status}</Text>
        </Flex>
      </Badge>
    );
  };

  // Count cancellations by status
  const getCounts = () => {
    const pending = workersCancellation.filter(item => !item.inProgress && !item.actionTaken).length;
    const inProgress = workersCancellation.filter(item => item.inProgress && !item.actionTaken && !item.pendingConfirmation).length;
    const pendingConfirmation = workersCancellation.filter(item => item.pendingConfirmation && !item.actionTaken).length;
    const completed = workersCancellation.filter(item => item.actionTaken).length;

    return { pending, inProgress, pendingConfirmation, completed, total: workersCancellation.length };
  };
  
  const counts = getCounts();
  
  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">Lista de Conversas Canceladas por Contratados</Heading>
        <Tag bg="#4bedffff" color="black" size="lg" borderRadius="full">
          <Flex align="center">
            <Icon as={FiBriefcase} mr={2} />
            <Text>{workersCancellation.length} Cancelamentos</Text>
          </Flex>
        </Tag>
      </Flex>

      {/* Status Summary */}
      <Flex 
        mb={4} 
        gap={3} 
        flexWrap="wrap"
        bg={filterBg}
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={filterBorderColor}
      >
        <Button 
          leftIcon={<WarningIcon />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'active' : 'pending')}
        >
          Aguardando ({counts.pending})
        </Button>
        
        <Button
          leftIcon={<TimeIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'inProgress' ? 'active' : 'inProgress')}
        >
          Em Andamento ({counts.inProgress})
        </Button>

        <Button
          leftIcon={<QuestionIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'pendingConfirmation' ? 'active' : 'pendingConfirmation')}
        >
          Aguardando Confirmação ({counts.pendingConfirmation})
        </Button>

        <Button
          leftIcon={<CheckIcon />}
          bg="#4bedffff"
          color="black"
          size="sm"
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'active' : 'completed')}
        >
          Concluído ({counts.completed})
        </Button>
        
        <Button 
          leftIcon={<FiRefreshCw />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={resetFilters}
          ml="auto"
        >
          Limpar Filtros
        </Button>
      </Flex>

      {/* Search Box */}
      <Flex mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar por opção ou razão..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
      </Flex>

      {loading && !selectedWorkerCancellation ? (
        <Center h="50vh">
          <Spinner size="xl" thickness="4px" color="blue.500" />
        </Center>
      ) : (
        <Box 
          overflowX="auto"
          borderWidth="1px"
          borderRadius="lg"
          borderColor={tableBorderColor}
        >
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>ID</Th>
                <Th>Opçao de Cancelamento</Th>
                <Th>Razão</Th>
                <Th>Data</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredCancellations.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={6}>
                    <Text color="gray.500">
                      {workersCancellation.length > 0 
                        ? 'Nenhum cancelamento encontrado com os filtros aplicados' 
                        : 'Nenhum cancelamento encontrado'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredCancellations.map((workerCancellation) => (
                  <Tr 
                    key={workerCancellation._id} 
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <Button
                        variant="link"
                        color="teal"
                        onClick={() => fetchWorkerCancellationById(workerCancellation._id)}
                        isTruncated
                        maxWidth="150px"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        {workerCancellation._id.substring(0, 8)}...
                      </Button>
                    </Td>
                    <Td>{workerCancellation.cancellationOption}</Td>
                    <Td isTruncated maxWidth="200px">{workerCancellation.reason}</Td>
                    <Td>{new Date(workerCancellation.createdAt).toLocaleDateString()}</Td>
                    <Td>
                      <StatusBadge
                        isCompleted={workerCancellation.actionTaken}
                        isInProgress={workerCancellation.inProgress}
                        isPendingConfirmation={workerCancellation.pendingConfirmation}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          leftIcon={<FiEye />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => fetchWorkerCancellationById(workerCancellation._id)}
                        >
                          Detalhes
                        </Button>
                        <Button
                          leftIcon={<FiMessageCircle />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => openAdminChat(workerCancellation.workerId)}
                        >
                          Chat
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Using our new modular component */}
      {selectedWorkerCancellation && (
        <WorkerCancellationDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          cancellationData={selectedWorkerCancellation}
          onActionTaken={handleActionTaken}
          onOpenChat={openAdminChat}
        />
      )}
      
      {/* Chat Modal */}
      <AdminChatModal
        userId={currentUserId}
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
      />
    </Box>
  );
};

export default WorkerCancellation;
